const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
class BrzSection extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.classList.add('brz-ready');
    this.hairObserver?.disconnect();
    const hair = this.querySelector('.brz-hair');
    if (hair && hair.dataset.hairMotion !== 'none' && !reduced()) {
      hair.classList.add('brz-hair-ready');
      this.hairObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) hair.classList.add('brz-hair-visible');
          else if (hair.dataset.hairMotion === 'loop') hair.classList.remove('brz-hair-visible');
        }
      }, { threshold: 0.25 });
      this.hairObserver.observe(hair);
      document.addEventListener('shopify:section:select', event => {
        if (`Brz-${event.detail?.sectionId}` !== this.id) return;
        hair.classList.remove('brz-hair-visible');
        // Commit the reset so selecting the section reliably replays the drawing.
        void hair.getBoundingClientRect();
        hair.classList.add('brz-hair-visible');
      }, { signal });
    }
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.classList.add('brz-seen');
        this.observer.disconnect();
      }
    }, { threshold: 0.06 });
    this.observer.observe(this);
    this.addEventListener('click', event => {
      if (event.target.closest('[data-brz-reviews]')) { event.preventDefault(); document.querySelector('.brz-reviews')?.scrollIntoView({ behavior: reduced() ? 'instant' : 'smooth' }); }
      if (event.target.closest('[data-back-top]')) window.scrollTo({ top: 0, behavior: reduced() ? 'instant' : 'smooth' });
    }, { signal });
    this.mediaObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting && this.dataset.videoAutoplay === 'true' && !document.hidden && !reduced() && !video.dataset.userPaused) video.play().catch(() => {});
        else video.pause();
      }
    }, { threshold: 0.6 });
    this.querySelectorAll('video').forEach(video => this.mediaObserver.observe(video));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.querySelectorAll('video').forEach(video => video.pause());
    }, { signal });
    this.querySelectorAll('video').forEach(video => {
      video.addEventListener('pointerdown', () => { video.dataset.userPaused = 'true'; }, { signal });
    });
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.mediaObserver?.disconnect(); this.hairObserver?.disconnect(); }
}
class BrzCarousel extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    const { signal } = this.abort;
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.paused = reduced();
    this.dots = this.querySelector('[data-dots]');
    this.dots?.replaceChildren();
    this.slides.forEach((slide, index) => {
      if (!this.dots) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `${this.dots.dataset.label} ${index + 1}`);
      button.addEventListener('click', () => this.go(index), { signal });
      this.dots.append(button);
    });
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1), { signal });
    this.querySelector('[data-pause]')?.addEventListener('click', event => {
      this.paused = !this.paused;
      const button = event.currentTarget;
      button.textContent = this.paused ? '▶' : 'Ⅱ';
      button.setAttribute('aria-label', this.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    }, { signal });
    this.addEventListener('shopify:block:select', event => {
      const slide = event.target.closest('.brz-slide');
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.paused = true;
      const pause = this.querySelector('[data-pause]');
      if (pause) { pause.textContent = '▶'; pause.setAttribute('aria-label', pause.dataset.playLabel); }
      this.go(index);
    }, { signal });
    this.track.addEventListener('scroll' , () => this.update(), { passive: true, signal });
    this.addEventListener('pointerenter', () => { this.hovered = true; }, { signal });
    this.addEventListener('pointerleave', () => { this.hovered = false; }, { signal });
    this.addEventListener('focusin', () => { this.focused = true; }, { signal });
    this.addEventListener('focusout', event => { this.focused = this.contains(event.relatedTarget); }, { signal });
    this.resize = new ResizeObserver(() => this.update()); this.resize.observe(this.track);
    this.visibility = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; }); this.visibility.observe(this);
    if (this.dataset.autoplay === 'true') this.timer = setInterval(() => {
      if (!this.paused && !this.hovered && !this.focused && this.visible && !document.hidden && !reduced()) this.step(1);
    }, Math.max(3, Number(this.dataset.interval) || 6) * 1000);
    this.update();
  }
  get current() {
    if (!this.slides.length) return 0;
    const left = this.track.getBoundingClientRect().left;
    return this.slides.reduce((best, slide, i) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(this.slides[best].getBoundingClientRect().left - left) ? i : best, 0);
  }
  go(index) {
    if (!this.slides.length) return;
    index = Math.max(0, Math.min(index, this.slides.length - 1));
    const offset = this.slides[index].getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    this.track.scrollTo({ left: offset, behavior: reduced() ? 'instant' : 'smooth' });
  }
  step(direction) {
    const atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 3;
    if (this.dataset.loop === 'true' && direction > 0 && atEnd) this.go(0);
    else if (this.dataset.loop === 'true' && direction < 0 && this.track.scrollLeft < 3) this.go(this.slides.length - 1);
    else this.go(this.current + direction);
  }
  update() {
    const current = this.current;
    [...(this.dots?.children || [])].forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
    const overflow = this.track.scrollWidth > this.track.clientWidth + 3;
    const controls = this.querySelector('.brz-carousel-controls');
    if (controls) controls.hidden = !overflow;
    for (const direction of ['prev', 'next']) {
      const button = this.querySelector(`[data-${direction}]`);
      if (button) button.disabled = !overflow || (this.dataset.loop !== 'true' && (direction === 'prev' ? this.track.scrollLeft < 3 : this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 3));
    }
  }
  disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); this.visibility?.disconnect(); clearInterval(this.timer); }
}
class BrzCompare extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.input.addEventListener('input', () => { this.touched = true; this.style.setProperty('--brz-split', `${this.input.value}%`); }, { signal: this.abort.signal });
    if (this.dataset.nudge === 'true' && !reduced()) {
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) return;
        this.observer.disconnect();
        const start = Number(this.input.value), time = performance.now();
        const animate = now => {
          if (!this.isConnected || this.touched || reduced()) return;
          const progress = Math.min((now - time) / 900, 1);
          this.style.setProperty('--brz-split', `${start + Math.sin(progress * Math.PI) * 4}%`);
          if (progress < 1) this.frame = requestAnimationFrame(animate);
        };
        this.frame = requestAnimationFrame(animate);
      }, { threshold: 0.5 });
      this.observer.observe(this);
    }
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame); }
}
for (const [tag, ctor] of [['brz-section', BrzSection], ['brz-carousel', BrzCarousel], ['brz-compare', BrzCompare]]) {
  if (!customElements.get(tag)) customElements.define(tag, ctor);
}
